
import { useState, useRef, useCallback } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

export function useAudioPlayback() {
  const [isSpeaking, setIsSpeaking] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const stopAudio = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsSpeaking(false);
    }
    
    // Arrêter aussi Web Speech API si active
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }
  }, []);

  // Fonction pour nettoyer le texte sans trop d'agression
  const cleanTextForTTS = useCallback((text: string): string => {
    if (!text) return text;
    
    try {
      return text
        // Nettoyage markdown minimal mais efficace
        .replace(/\*\*(.*?)\*\*/g, '$1') // Texte en gras
        .replace(/\*(.*?)\*/g, '$1') // Texte en italique
        .replace(/`(.*?)`/g, '$1') // Code inline
        .replace(/\[(.*?)\]\(.*?\)/g, '$1') // Liens markdown
        .replace(/#{1,6}\s+/g, '') // Titres markdown
        .replace(/^\s*[-*+]\s+/gm, '') // Listes
        .replace(/^\s*\d+\.\s+/gm, '') // Listes numérotées
        
        // Nettoyer uniquement les caractères vraiment problématiques
        .replace(/[""]/g, '"') // Guillemets courbes
        .replace(/['']/g, "'") // Apostrophes courbes
        .replace(/–/g, '-') // Tirets longs
        .replace(/—/g, ' - ') // Tirets très longs
        .replace(/…/g, '...') // Points de suspension
        
        // Améliorer légèrement la ponctuation pour la lecture
        .replace(/\s+/g, ' ')
        .trim();
    } catch (error) {
      console.error("Erreur nettoyage texte pour TTS:", error);
      return text; // Retourner le texte original en cas d'erreur
    }
  }, []);

  // Fonction pour segmenter intelligemment le texte long
  const segmentText = useCallback((text: string, maxLength: number = 800): string[] => {
    if (text.length <= maxLength) return [text];
    
    const segments: string[] = [];
    const sentences = text.split(/[.!?]+/).filter(s => s.trim());
    
    let currentSegment = '';
    
    for (const sentence of sentences) {
      const trimmedSentence = sentence.trim();
      if (!trimmedSentence) continue;
      
      const potentialSegment = currentSegment + (currentSegment ? '. ' : '') + trimmedSentence + '.';
      
      if (potentialSegment.length <= maxLength) {
        currentSegment = potentialSegment;
      } else {
        if (currentSegment) {
          segments.push(currentSegment);
        }
        currentSegment = trimmedSentence + '.';
      }
    }
    
    if (currentSegment) {
      segments.push(currentSegment);
    }
    
    return segments.length > 0 ? segments : [text.substring(0, maxLength)];
  }, []);

  // Validation de la qualité audio
  const validateAudioResponse = useCallback((audioContent: string, originalTextLength: number): boolean => {
    if (!audioContent || audioContent.length < 100) {
      console.warn("⚠️ Audio généré trop court, possible coupure");
      return false;
    }
    
    // Estimation grossière : plus de texte = plus d'audio
    const expectedMinSize = Math.max(1000, originalTextLength * 50);
    if (audioContent.length < expectedMinSize) {
      console.warn(`⚠️ Audio possiblement tronqué - Attendu: ~${expectedMinSize}, Reçu: ${audioContent.length}`);
      return false;
    }
    
    return true;
  }, []);

  // Fonction principale pour jouer l'audio avec gestion améliorée
  const playAudioResponse = useCallback(async (text: string, useElevenLabs: boolean = true, language: string = 'fr') => {
    try {
      console.log("🎵 Démarrage lecture audio:", { 
        textLength: text.length, 
        useElevenLabs, 
        language,
        preview: text.substring(0, 100) + "..."
      });
      
      stopAudio(); // Arrêter tout audio en cours
      setIsSpeaking(true);

      if (useElevenLabs) {
        console.log("🎵 Tentative génération ElevenLabs...");
        
        try {
          // Nettoyer le texte SANS réduction drastique de longueur
          const cleanedText = cleanTextForTTS(text);
          console.log("🧹 Texte nettoyé:", { 
            originalLength: text.length, 
            cleanedLength: cleanedText.length,
            reduction: `${((text.length - cleanedText.length) / text.length * 100).toFixed(1)}%`
          });

          // Segmenter SEULEMENT si vraiment nécessaire (>1000 chars)
          const segments = cleanedText.length > 1000 ? segmentText(cleanedText, 900) : [cleanedText];
          
          if (segments.length > 1) {
            console.log(`📝 Texte segmenté en ${segments.length} parties pour éviter les coupures`);
          }

          // Jouer chaque segment séquentiellement
          for (let i = 0; i < segments.length; i++) {
            console.log(`🎵 Génération segment ${i + 1}/${segments.length}:`, {
              segmentLength: segments[i].length,
              preview: segments[i].substring(0, 50) + "..."
            });

            const { data, error } = await supabase.functions.invoke('elevenlabs-tts', {
              body: {
                text: segments[i],
                language: language,
                useElevenLabs: true
              }
            });

            if (error) {
              console.error(`❌ Erreur ElevenLabs segment ${i + 1}:`, error);
              throw new Error(error.message || "Service ElevenLabs indisponible");
            }

            if (data?.audioContent && !data?.fallback) {
              // Valider la qualité de l'audio
              const isValidAudio = validateAudioResponse(data.audioContent, segments[i].length);
              
              if (!isValidAudio) {
                console.warn(`⚠️ Segment ${i + 1} possiblement tronqué, fallback vers Web Speech`);
                throw new Error("Audio tronqué détecté");
              }

              console.log(`✅ Segment ${i + 1} généré avec succès:`, {
                audioSize: data.audioContent.length,
                textLength: segments[i].length,
                ratio: (data.audioContent.length / segments[i].length).toFixed(2)
              });
              
              await playBase64Audio(data.audioContent);
              
              // Pause courte entre segments si multiple
              if (segments.length > 1 && i < segments.length - 1) {
                await new Promise(resolve => setTimeout(resolve, 500));
              }
            } else {
              console.warn(`⚠️ ElevenLabs en fallback pour segment ${i + 1}, utilisation Web Speech`);
              throw new Error("ElevenLabs en mode fallback");
            }
          }
          
          console.log("✅ Lecture ElevenLabs complète terminée avec succès");
          return;
          
        } catch (elevenLabsError) {
          console.error("❌ Échec ElevenLabs:", elevenLabsError);
          
          // Messages d'erreur simplifiés
          if (elevenLabsError.message?.includes('quota') || elevenLabsError.message?.includes('429')) {
            console.log("📢 Quota ElevenLabs atteint, fallback vers Web Speech");
          } else if (elevenLabsError.message?.includes('401') || elevenLabsError.message?.includes('unauthorized')) {
            console.log("📢 Clé API ElevenLabs invalide, fallback vers Web Speech");
          } else {
            console.log("📢 Service ElevenLabs temporairement indisponible, fallback vers Web Speech");
          }
          
          // Fallback vers Web Speech API avec le texte complet nettoyé
          console.log("🔄 Fallback vers Web Speech API avec texte complet...");
          playWebSpeechAudio(cleanTextForTTS(text), language);
          return;
        }
      }

      // Utilisation directe de Web Speech API
      console.log("🔊 Utilisation Web Speech API...");
      playWebSpeechAudio(cleanTextForTTS(text), language);

    } catch (error) {
      console.error("❌ Erreur audio globale:", error);
      setIsSpeaking(false);
      toast.error("Erreur lors de la lecture audio");
    }
  }, [cleanTextForTTS, segmentText, validateAudioResponse]);

  // Fonction pour jouer l'audio base64 d'ElevenLabs avec détection améliorée
  const playBase64Audio = useCallback(async (base64Audio: string) => {
    return new Promise<void>((resolve, reject) => {
      try {
        if (!base64Audio || base64Audio.length === 0) {
          throw new Error("Audio base64 vide");
        }

        // Créer un blob audio depuis base64
        const binaryString = atob(base64Audio);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        
        const audioBlob = new Blob([bytes], { type: 'audio/mpeg' });
        
        if (audioBlob.size === 0) {
          throw new Error("Blob audio vide après conversion");
        }
        
        const audioUrl = URL.createObjectURL(audioBlob);
        
        // Créer et configurer l'élément audio
        const audio = new Audio(audioUrl);
        audioRef.current = audio;
        
        audio.onloadeddata = () => {
          console.log("✅ Audio ElevenLabs chargé:", {
            duration: audio.duration,
            blobSize: audioBlob.size
          });
        };
        
        audio.onended = () => {
          console.log("✅ Lecture ElevenLabs terminée normalement");
          setIsSpeaking(false);
          URL.revokeObjectURL(audioUrl);
          resolve();
        };
        
        audio.onerror = (error) => {
          console.error("❌ Erreur lecture ElevenLabs:", error);
          setIsSpeaking(false);
          URL.revokeObjectURL(audioUrl);
          reject(new Error("Erreur lecture audio ElevenLabs"));
        };
        
        // Surveiller si l'audio s'arrête prématurément
        audio.ontimeupdate = () => {
          if (audio.currentTime > 0 && audio.duration > 0) {
            const progress = (audio.currentTime / audio.duration) * 100;
            if (progress > 95) { // Proche de la fin
              console.log("🎵 Audio ElevenLabs proche de la fin:", `${progress.toFixed(1)}%`);
            }
          }
        };
        
        // Jouer l'audio avec gestion d'erreur
        audio.play().catch(error => {
          console.error("❌ Échec play() ElevenLabs:", error);
          setIsSpeaking(false);
          URL.revokeObjectURL(audioUrl);
          reject(error);
        });
        
      } catch (error) {
        console.error("❌ Erreur traitement base64:", error);
        setIsSpeaking(false);
        reject(error);
      }
    });
  }, []);

  // Fonction Web Speech API (fallback) avec gestion complète du texte
  const playWebSpeechAudio = useCallback((text: string, language: string = 'fr') => {
    if (!('speechSynthesis' in window)) {
      console.error("❌ Web Speech API non supportée");
      setIsSpeaking(false);
      toast.error("Synthèse vocale non supportée par votre navigateur");
      return;
    }

    try {
      // Arrêter toute synthèse en cours
      window.speechSynthesis.cancel();
      
      // Segmenter le texte pour Web Speech (limite ~160 chars par utterance)
      const segments = segmentText(text, 160);
      
      console.log("🔊 Web Speech:", {
        totalLength: text.length,
        segments: segments.length,
        language: language
      });
      
      let currentSegment = 0;
      
      const speakSegment = () => {
        if (currentSegment >= segments.length) {
          console.log("✅ Web Speech terminé complètement");
          setIsSpeaking(false);
          return;
        }
        
        const utterance = new SpeechSynthesisUtterance(segments[currentSegment]);
        
        // Configuration de la langue
        const langMap: Record<string, string> = {
          'fr': 'fr-FR',
          'en': 'en-US', 
          'es': 'es-ES',
          'ht': 'fr-FR', // Fallback vers français pour le créole
          'ar': 'ar-SA'
        };
        
        utterance.lang = langMap[language] || 'fr-FR';
        utterance.rate = 0.9;
        utterance.pitch = 1.0;
        utterance.volume = 1.0;

        utterance.onstart = () => {
          console.log(`🔊 Web Speech segment ${currentSegment + 1}/${segments.length} démarré`);
        };

        utterance.onend = () => {
          console.log(`✅ Web Speech segment ${currentSegment + 1}/${segments.length} terminé`);
          currentSegment++;
          // Courte pause entre segments
          setTimeout(speakSegment, 100);
        };

        utterance.onerror = (error) => {
          console.error(`❌ Erreur Web Speech segment ${currentSegment + 1}:`, error);
          setIsSpeaking(false);
          toast.error("Erreur de synthèse vocale");
        };

        window.speechSynthesis.speak(utterance);
      };
      
      speakSegment();
      
    } catch (error) {
      console.error("❌ Erreur Web Speech setup:", error);
      setIsSpeaking(false);
      toast.error("Erreur configuration vocale");
    }
  }, [segmentText]);

  return {
    isSpeaking,
    playAudioResponse,
    stopAudio,
    playWebSpeechAudio
  };
}
